import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  CreditCard,
  TrendingUp,
  Activity,
  DollarSign,
  MessageSquare,
  Image,
  Mic,
  Shield,
  AlertTriangle,
  CheckCircle,
  Search,
  Filter,
  MoreVertical,
  UserX,
  Crown,
  RefreshCw,
  Loader2,
  ChevronRight,
  Clock,
  Zap,
  BarChart3,
  PieChart,
  Calendar,
  Palette,
  Wand2
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin Panel Dashboard
 * 
 * Comprehensive admin interface for platform management:
 * - User management
 * - Subscription analytics
 * - Platform metrics
 * - System monitoring
 */
export default function AdminPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Dashboard data
  const [dashboardData, setDashboardData] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [userSearch, setUserSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [dateRange, setDateRange] = useState(30); // days

  const loadDashboard = useCallback(async () => {
    setRefreshing(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
      }
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setRefreshing(false);
    }
  }, [token]);

  const verifyAdminAccess = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setIsAdmin(data.is_admin);
        
        if (data.is_admin) {
          await loadDashboard();
        }
      }
    } catch (error) {
      console.error('Admin verification failed:', error);
    } finally {
      setLoading(false);
    }
  }, [token, loadDashboard]);

  useEffect(() => {
    verifyAdminAccess();
  }, [verifyAdminAccess]);

  const loadAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/admin/analytics?dau_days=${dateRange}&revenue_days=${dateRange}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setAnalyticsData(data);
      }
    } catch (error) {
      console.error('Failed to load analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setAnalyticsLoading(false);
    }
  }, [token, dateRange]);

  const loadUsers = useCallback(async (search = '') => {
    try {
      const url = new URL(`${API_URL}/api/admin/users`);
      if (search) url.searchParams.set('search', search);
      url.searchParams.set('limit', '20');
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
        setUsersTotal(data.total);
      }
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  }, [token]);

  const updateUserStatus = async (userId, status, reason = '') => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status, reason })
      });
      
      if (response.ok) {
        toast.success(`User status updated to ${status}`);
        loadUsers(userSearch);
      }
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  useEffect(() => {
    if (activeTab === 'users' && isAdmin) {
      loadUsers(userSearch);
    }
    if (activeTab === 'analytics' && isAdmin) {
      loadAnalytics();
    }
  }, [activeTab, userSearch, isAdmin, dateRange, loadUsers, loadAnalytics]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--color-primary)]" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center p-4">
        <motion.div
          className="max-w-md w-full bg-[var(--bg-card)] rounded-2xl border border-[var(--border-color)] p-8 text-center"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
            <Shield className="w-10 h-10 text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Access Denied</h1>
          <p className="text-[var(--text-secondary)] mb-6">
            You don't have admin privileges to access this page.
          </p>
          <button
            onClick={() => navigate('/chat')}
            className="px-6 py-3 rounded-xl bg-[var(--color-primary)] text-white font-semibold"
          >
            Go Back to AIRA
          </button>
        </motion.div>
      </div>
    );
  }

  const { platform, subscriptions, usage, revenue, system } = dashboardData || {};

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Shield className="w-8 h-8 text-[var(--color-primary)]" />
              Admin Panel
            </h1>
            <p className="text-[var(--text-secondary)] mt-1">
              Monitor and manage AIRA ECHO platform
            </p>
          </div>
          <button
            onClick={loadDashboard}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[var(--bg-card)] border border-[var(--border-color)] text-white hover:bg-[var(--bg-hover)] transition-all"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2" data-testid="admin-tabs">
          {[
            { id: 'overview', label: 'Overview', icon: Activity },
            { id: 'users', label: 'Users', icon: Users },
            { id: 'subscriptions', label: 'Subscriptions', icon: CreditCard },
            { id: 'analytics', label: 'Analytics', icon: TrendingUp },
            { id: 'system', label: 'System', icon: Zap }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`admin-tab-${tab.id}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-[var(--color-primary)] text-white'
                  : 'bg-[var(--bg-card)] text-[var(--text-secondary)] hover:text-white'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && dashboardData && (
          <div className="space-y-6">
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <MetricCard
                title="Total Users"
                value={platform?.total_users || 0}
                icon={Users}
                change={`+${platform?.new_users_this_week || 0} this week`}
                color="blue"
              />
              <MetricCard
                title="Active Today"
                value={platform?.daily_active_users || 0}
                icon={Activity}
                change={`${platform?.weekly_active_users || 0} this week`}
                color="green"
              />
              <MetricCard
                title="Paid Subscribers"
                value={subscriptions?.paid_subscribers || 0}
                icon={Crown}
                change={`${subscriptions?.total_active || 0} total active`}
                color="purple"
              />
              <MetricCard
                title="Monthly Revenue"
                value={`$${(revenue?.monthly_revenue || 0).toFixed(2)}`}
                icon={DollarSign}
                change={`$${(revenue?.total_revenue || 0).toFixed(2)} total`}
                color="amber"
              />
            </div>

            {/* Usage Metrics */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border-color)] p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Today's AI Usage</h3>
                <div className="grid grid-cols-2 gap-4">
                  <UsageMetric icon={MessageSquare} label="AI Chats" value={usage?.today?.total_ai_chats || 0} />
                  <UsageMetric icon={Image} label="Images Generated" value={usage?.today?.total_image_generations || 0} />
                  <UsageMetric icon={Mic} label="Voice Minutes" value={Math.round(usage?.today?.total_voice_minutes || 0)} />
                  <UsageMetric icon={Activity} label="Active Users" value={usage?.today?.active_users || 0} />
                </div>
              </div>

              <div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border-color)] p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Subscription Distribution</h3>
                <div className="space-y-3">
                  {Object.entries(subscriptions?.by_tier || {}).map(([tier, data]) => (
                    <div key={tier} className="flex items-center justify-between">
                      <span className="text-[var(--text-secondary)] capitalize">{tier}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-32 h-2 bg-[var(--bg-tertiary)] rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-[var(--color-primary)] rounded-full"
                            style={{ width: `${(data.active / Math.max(subscriptions?.total_active || 1, 1)) * 100}%` }}
                          />
                        </div>
                        <span className="text-white font-medium w-12 text-right">{data.active}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* System Health */}
            <div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border-color)] p-6">
              <h3 className="text-lg font-semibold text-white mb-4">System Health</h3>
              <div className="flex flex-wrap gap-4">
                <HealthIndicator label="Database" status={system?.database} />
                <HealthIndicator label="API" status={system?.api} />
                <HealthIndicator label="Uptime" status="healthy" value={system?.uptime} />
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            {/* Search */}
            <div className="flex gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--text-tertiary)]" />
                <input
                  type="text"
                  placeholder="Search users by email or name..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-[var(--bg-card)] border border-[var(--border-color)] text-white placeholder-[var(--text-tertiary)] focus:border-[var(--color-primary)] outline-none"
                />
              </div>
              <button className="px-4 py-3 rounded-xl bg-[var(--bg-card)] border border-[var(--border-color)] text-white flex items-center gap-2">
                <Filter className="w-4 h-4" />
                Filter
              </button>
            </div>

            {/* Users Table */}
            <div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border-color)] overflow-hidden">
              <div className="p-4 border-b border-[var(--border-color)]">
                <p className="text-[var(--text-secondary)]">
                  Showing {users.length} of {usersTotal} users
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[var(--border-color)]">
                      <th className="text-left p-4 text-[var(--text-secondary)] font-medium">User</th>
                      <th className="text-left p-4 text-[var(--text-secondary)] font-medium">Email</th>
                      <th className="text-left p-4 text-[var(--text-secondary)] font-medium">Plan</th>
                      <th className="text-left p-4 text-[var(--text-secondary)] font-medium">Status</th>
                      <th className="text-left p-4 text-[var(--text-secondary)] font-medium">Joined</th>
                      <th className="text-left p-4 text-[var(--text-secondary)] font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.user_id} className="border-b border-[var(--border-color)] hover:bg-[var(--bg-hover)]">
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-secondary)] flex items-center justify-center text-white font-bold">
                              {user.name?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase()}
                            </div>
                            <span className="text-white font-medium">{user.name || 'N/A'}</span>
                          </div>
                        </td>
                        <td className="p-4 text-[var(--text-secondary)]">{user.email}</td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            user.subscription?.tier === 'pro' ? 'bg-purple-500/20 text-purple-400' :
                            user.subscription?.tier === 'creator' ? 'bg-blue-500/20 text-blue-400' :
                            user.subscription?.tier === 'enterprise' ? 'bg-amber-500/20 text-amber-400' :
                            'bg-gray-500/20 text-gray-400'
                          }`}>
                            {user.subscription?.tier || 'free'}
                          </span>
                        </td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            user.status === 'active' ? 'bg-green-500/20 text-green-400' :
                            user.status === 'suspended' ? 'bg-orange-500/20 text-orange-400' :
                            'bg-red-500/20 text-red-400'
                          }`}>
                            {user.status || 'active'}
                          </span>
                        </td>
                        <td className="p-4 text-[var(--text-secondary)]">
                          {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setSelectedUser(user)}
                              className="p-2 rounded-lg hover:bg-[var(--bg-tertiary)] text-[var(--text-secondary)] hover:text-white"
                            >
                              <ChevronRight className="w-4 h-4" />
                            </button>
                            {user.status !== 'suspended' ? (
                              <button
                                onClick={() => updateUserStatus(user.user_id, 'suspended')}
                                className="p-2 rounded-lg hover:bg-orange-500/20 text-[var(--text-secondary)] hover:text-orange-400"
                                title="Suspend User"
                              >
                                <UserX className="w-4 h-4" />
                              </button>
                            ) : (
                              <button
                                onClick={() => updateUserStatus(user.user_id, 'active')}
                                className="p-2 rounded-lg hover:bg-green-500/20 text-[var(--text-secondary)] hover:text-green-400"
                                title="Activate User"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Subscriptions Tab */}
        {activeTab === 'subscriptions' && dashboardData && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {['free', 'creator', 'pro', 'enterprise'].map((tier) => {
                const data = subscriptions?.by_tier?.[tier] || { total: 0, active: 0 };
                return (
                  <div key={tier} className="bg-[var(--bg-card)] rounded-xl border border-[var(--border-color)] p-6">
                    <h3 className="text-[var(--text-secondary)] text-sm uppercase tracking-wide mb-2">{tier}</h3>
                    <p className="text-3xl font-bold text-white">{data.active}</p>
                    <p className="text-sm text-[var(--text-tertiary)]">{data.total} total</p>
                  </div>
                );
              })}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border-color)] p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Revenue Overview</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-[var(--text-secondary)]">Total Revenue</span>
                    <span className="text-2xl font-bold text-white">${(revenue?.total_revenue || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[var(--text-secondary)]">This Month</span>
                    <span className="text-xl font-bold text-green-400">${(revenue?.monthly_revenue || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[var(--text-secondary)]">Total Transactions</span>
                    <span className="text-white">{revenue?.total_transactions || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[var(--text-secondary)]">Avg Transaction</span>
                    <span className="text-white">${(revenue?.average_transaction || 0).toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border-color)] p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Revenue by Plan</h3>
                <div className="space-y-3">
                  {Object.entries(revenue?.by_plan || {}).map(([plan, data]) => (
                    <div key={plan} className="flex justify-between items-center">
                      <span className="text-[var(--text-secondary)] capitalize">{plan}</span>
                      <div className="text-right">
                        <span className="text-white font-medium">${data.revenue?.toFixed(2)}</span>
                        <span className="text-[var(--text-tertiary)] text-sm ml-2">({data.transactions} txns)</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Analytics Tab - Enhanced */}
        {activeTab === 'analytics' && (
          <div className="space-y-6">
            {/* Date Range Filter */}
            <div className="flex items-center gap-4" data-testid="analytics-date-range-filter">
              <div className="flex items-center gap-2 text-[var(--text-secondary)]">
                <Calendar className="w-4 h-4" />
                <span>Date Range:</span>
              </div>
              <div className="flex gap-2">
                {[7, 14, 30, 90].map((days) => (
                  <button
                    key={days}
                    onClick={() => setDateRange(days)}
                    data-testid={`date-range-${days}d`}
                    className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                      dateRange === days
                        ? 'bg-[var(--color-primary)] text-white'
                        : 'bg-[var(--bg-card)] text-[var(--text-secondary)] hover:text-white'
                    }`}
                  >
                    {days}d
                  </button>
                ))}
              </div>
              {analyticsLoading && <Loader2 className="w-4 h-4 animate-spin text-[var(--color-primary)]" data-testid="analytics-loading-spinner" />}
            </div>

            {analyticsData ? (
              <>
                {/* DAU/WAU/MAU Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="analytics-cards-container">
                  <MetricCard
                    title="Daily Active Users"
                    value={analyticsData.dau?.today || 0}
                    icon={Users}
                    change={`${analyticsData.dau?.growth_rate_7d > 0 ? '+' : ''}${analyticsData.dau?.growth_rate_7d || 0}% vs last week`}
                    color="blue"
                    testId="dau-card"
                  />
                  <MetricCard
                    title="Weekly Active Users"
                    value={analyticsData.dau?.wau || 0}
                    icon={Activity}
                    change={`Avg: ${analyticsData.dau?.avg_7_days || 0}/day`}
                    color="green"
                    testId="wau-card"
                  />
                  <MetricCard
                    title="Monthly Active Users"
                    value={analyticsData.dau?.mau || 0}
                    icon={TrendingUp}
                    change={`Peak: ${analyticsData.dau?.peak_dau || 0}`}
                    color="purple"
                    testId="mau-card"
                  />
                  <MetricCard
                    title="Total Images"
                    value={analyticsData.ai_usage?.images?.total || 0}
                    icon={Image}
                    change={`+${analyticsData.ai_usage?.images?.this_month || 0} this month`}
                    color="amber"
                    testId="total-images-card"
                  />
                </div>

                {/* Charts Row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* DAU Trend Chart */}
                  <div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border-color)] p-6" data-testid="dau-trend-chart">
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-[var(--color-primary)]" />
                      Daily Active Users Trend
                    </h3>
                    <div className="h-64">
                      <SimpleLineChart 
                        data={analyticsData.dau?.trend_data || []} 
                        dataKey="dau"
                        color="var(--color-primary)"
                      />
                    </div>
                  </div>

                  {/* Style Usage Pie Chart */}
                  <div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border-color)] p-6" data-testid="style-usage-chart">
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                      <PieChart className="w-5 h-5 text-purple-400" />
                      Image Style Usage
                    </h3>
                    <div className="space-y-3" data-testid="style-usage-breakdown">
                      {(analyticsData.ai_usage?.style_usage || []).slice(0, 6).map((style, idx) => {
                        const maxCount = analyticsData.ai_usage?.style_usage?.[0]?.count || 1;
                        const percentage = Math.round((style.count / maxCount) * 100);
                        const colors = ['bg-blue-500', 'bg-purple-500', 'bg-pink-500', 'bg-amber-500', 'bg-green-500', 'bg-cyan-500'];
                        return (
                          <div key={style.style} className="flex items-center gap-3" data-testid={`style-item-${style.style}`}>
                            <div className={`w-3 h-3 rounded-full ${colors[idx % colors.length]}`} />
                            <span className="flex-1 text-[var(--text-secondary)] capitalize">
                              {style.style?.replace('-', ' ') || 'Default'}
                            </span>
                            <div className="w-24 h-2 bg-[var(--bg-tertiary)] rounded-full overflow-hidden">
                              <div 
                                className={`h-full ${colors[idx % colors.length]} rounded-full`}
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                            <span className="w-12 text-right text-white font-medium">{style.count}</span>
                          </div>
                        );
                      })}
                      {(!analyticsData.ai_usage?.style_usage || analyticsData.ai_usage.style_usage.length === 0) && (
                        <p className="text-[var(--text-tertiary)] text-center py-8" data-testid="no-style-data">No style usage data yet</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Revenue Section */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border-color)] p-6" data-testid="revenue-overview">
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-green-400" />
                      Revenue Overview
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <p className="text-[var(--text-tertiary)] text-sm">Total Revenue</p>
                        <p className="text-3xl font-bold text-white" data-testid="total-revenue">
                          ${(analyticsData.revenue?.total_revenue || 0).toFixed(2)}
                        </p>
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <p className="text-[var(--text-tertiary)] text-xs">Today</p>
                          <p className="text-lg font-semibold text-green-400" data-testid="revenue-today">
                            ${(analyticsData.revenue?.revenue_today || 0).toFixed(2)}
                          </p>
                        </div>
                        <div>
                          <p className="text-[var(--text-tertiary)] text-xs">This Week</p>
                          <p className="text-lg font-semibold text-blue-400" data-testid="revenue-week">
                            ${(analyticsData.revenue?.revenue_this_week || 0).toFixed(2)}
                          </p>
                        </div>
                        <div>
                          <p className="text-[var(--text-tertiary)] text-xs">This Month</p>
                          <p className="text-lg font-semibold text-purple-400" data-testid="revenue-month">
                            ${(analyticsData.revenue?.revenue_this_month || 0).toFixed(2)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* AI Usage Stats */}
                  <div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border-color)] p-6" data-testid="ai-usage-stats">
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                      <Wand2 className="w-5 h-5 text-pink-400" />
                      AI Usage Stats
                    </h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Image className="w-4 h-4 text-[var(--color-primary)]" />
                          <span className="text-[var(--text-secondary)]">Images Today</span>
                        </div>
                        <span className="text-white font-semibold" data-testid="images-today-stat">{analyticsData.ai_usage?.images?.today || 0}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <MessageSquare className="w-4 h-4 text-blue-400" />
                          <span className="text-[var(--text-secondary)]">Total Chats</span>
                        </div>
                        <span className="text-white font-semibold" data-testid="total-chats-stat">{analyticsData.ai_usage?.prompts?.total_chats || 0}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Mic className="w-4 h-4 text-green-400" />
                          <span className="text-[var(--text-secondary)]">Voice Minutes</span>
                        </div>
                        <span className="text-white font-semibold">{analyticsData.ai_usage?.prompts?.total_voice_minutes || 0}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-purple-400" />
                          <span className="text-[var(--text-secondary)]">Unique Creators</span>
                        </div>
                        <span className="text-white font-semibold">{analyticsData.ai_usage?.images?.unique_creators || 0}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Palette className="w-4 h-4 text-amber-400" />
                          <span className="text-[var(--text-secondary)]">Avg/User</span>
                        </div>
                        <span className="text-white font-semibold">{analyticsData.ai_usage?.images?.avg_per_user || 0}</span>
                      </div>
                    </div>
                  </div>

                  {/* Edit Operations Usage */}
                  <div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border-color)] p-6">
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                      <Wand2 className="w-5 h-5 text-orange-400" />
                      Edit Operations
                    </h3>
                    <div className="space-y-3">
                      {(analyticsData.ai_usage?.edit_usage || []).slice(0, 5).map((edit) => (
                        <div key={edit.operation} className="flex items-center justify-between">
                          <span className="text-[var(--text-secondary)] capitalize">
                            {edit.operation?.replace('_', ' ')}
                          </span>
                          <span className="text-white font-semibold">{edit.count}</span>
                        </div>
                      ))}
                      {(!analyticsData.ai_usage?.edit_usage || analyticsData.ai_usage.edit_usage.length === 0) && (
                        <p className="text-[var(--text-tertiary)] text-center py-4">No edits yet</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Top Active Users Table */}
                <div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border-color)] overflow-hidden" data-testid="top-users-table">
                  <div className="p-4 border-b border-[var(--border-color)]">
                    <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                      <Crown className="w-5 h-5 text-amber-400" />
                      Top Active Users
                    </h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-[var(--border-color)]">
                          <th className="text-left p-4 text-[var(--text-secondary)] font-medium">Rank</th>
                          <th className="text-left p-4 text-[var(--text-secondary)] font-medium">User</th>
                          <th className="text-left p-4 text-[var(--text-secondary)] font-medium">Images Created</th>
                          <th className="text-left p-4 text-[var(--text-secondary)] font-medium">Last Active</th>
                        </tr>
                      </thead>
                      <tbody data-testid="top-users-tbody">
                        {(analyticsData.top_users || []).map((user, idx) => (
                          <tr key={user.user_id} className="border-b border-[var(--border-color)] hover:bg-[var(--bg-hover)]" data-testid={`top-user-row-${idx}`}>
                            <td className="p-4">
                              <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                                idx === 0 ? 'bg-amber-500/20 text-amber-400' :
                                idx === 1 ? 'bg-gray-400/20 text-gray-300' :
                                idx === 2 ? 'bg-orange-500/20 text-orange-400' :
                                'bg-[var(--bg-tertiary)] text-[var(--text-secondary)]'
                              }`}>
                                {idx + 1}
                              </span>
                            </td>
                            <td className="p-4">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-secondary)] flex items-center justify-center text-white font-bold">
                                  {user.name?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase()}
                                </div>
                                <div>
                                  <p className="text-white font-medium">{user.name || 'N/A'}</p>
                                  <p className="text-[var(--text-tertiary)] text-sm">{user.email}</p>
                                </div>
                              </div>
                            </td>
                            <td className="p-4">
                              <span className="text-white font-semibold" data-testid={`user-image-count-${idx}`}>{user.image_count}</span>
                            </td>
                            <td className="p-4 text-[var(--text-secondary)]">
                              {user.last_activity ? new Date(user.last_activity).toLocaleDateString() : 'N/A'}
                            </td>
                          </tr>
                        ))}
                        {(!analyticsData.top_users || analyticsData.top_users.length === 0) && (
                          <tr>
                            <td colSpan={4} className="p-8 text-center text-[var(--text-tertiary)]" data-testid="no-top-users">
                              No user activity data yet
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* API Daily Usage */}
                <div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border-color)] p-6" data-testid="api-daily-usage-table">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Activity className="w-5 h-5 text-cyan-400" />
                    Daily API Usage (Last 7 Days)
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-[var(--border-color)]">
                          <th className="text-left p-3 text-[var(--text-secondary)] font-medium">Date</th>
                          <th className="text-right p-3 text-[var(--text-secondary)] font-medium">Images</th>
                          <th className="text-right p-3 text-[var(--text-secondary)] font-medium">Chats</th>
                          <th className="text-right p-3 text-[var(--text-secondary)] font-medium">Voice (min)</th>
                          <th className="text-right p-3 text-[var(--text-secondary)] font-medium">Active Users</th>
                        </tr>
                      </thead>
                      <tbody data-testid="api-daily-tbody">
                        {(analyticsData.api_daily || []).map((day) => (
                          <tr key={day.date} className="border-b border-[var(--border-color)]" data-testid={`api-daily-row-${day.date}`}>
                            <td className="p-3 text-white">{day.date}</td>
                            <td className="p-3 text-right text-[var(--color-primary)] font-semibold">{day.image_generations}</td>
                            <td className="p-3 text-right text-blue-400">{day.chat_requests}</td>
                            <td className="p-3 text-right text-green-400">{day.voice_minutes}</td>
                            <td className="p-3 text-right text-purple-400">{day.active_users}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-[var(--color-primary)]" />
              </div>
            )}
          </div>
        )}

        {/* System Tab */}
        {activeTab === 'system' && dashboardData && (
          <div className="space-y-6">
            <div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border-color)] p-6">
              <h3 className="text-lg font-semibold text-white mb-4">System Status</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <HealthIndicator label="Database" status={system?.database} large />
                <HealthIndicator label="API Server" status={system?.api} large />
                <div className="p-4 rounded-lg bg-[var(--bg-tertiary)]">
                  <span className="text-[var(--text-secondary)] text-sm">System Uptime</span>
                  <p className="text-2xl font-bold text-white mt-1">{system?.uptime || 'N/A'}</p>
                </div>
              </div>
            </div>

            <div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border-color)] p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Last Checked</h3>
              <p className="text-[var(--text-secondary)]">
                {system?.last_checked ? new Date(system.last_checked).toLocaleString() : 'N/A'}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// HELPER COMPONENTS
// =============================================================================

function MetricCard({ title, value, icon: Icon, change, color, testId }) {
  const colorClasses = {
    blue: 'from-blue-500/20 to-blue-600/20 text-blue-400',
    green: 'from-green-500/20 to-green-600/20 text-green-400',
    purple: 'from-purple-500/20 to-purple-600/20 text-purple-400',
    amber: 'from-amber-500/20 to-amber-600/20 text-amber-400'
  };

  return (
    <motion.div
      className="bg-[var(--bg-card)] rounded-xl border border-[var(--border-color)] p-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid={testId}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[var(--text-secondary)] text-sm">{title}</p>
          <p className="text-3xl font-bold text-white mt-1" data-testid={testId ? `${testId}-value` : undefined}>{value}</p>
          <p className="text-[var(--text-tertiary)] text-sm mt-1" data-testid={testId ? `${testId}-change` : undefined}>{change}</p>
        </div>
        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colorClasses[color]} flex items-center justify-center`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </motion.div>
  );
}

function UsageMetric({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-[var(--bg-tertiary)]">
      <Icon className="w-5 h-5 text-[var(--color-primary)]" />
      <div>
        <p className="text-white font-semibold">{value}</p>
        <p className="text-[var(--text-tertiary)] text-sm">{label}</p>
      </div>
    </div>
  );
}

function HealthIndicator({ label, status, value, large = false }) {
  const isHealthy = status === 'healthy';
  
  return (
    <div className={`flex items-center gap-3 ${large ? 'p-4 rounded-lg bg-[var(--bg-tertiary)]' : ''}`}>
      <div className={`w-3 h-3 rounded-full ${isHealthy ? 'bg-green-400' : 'bg-red-400'} animate-pulse`} />
      <span className={`${large ? 'text-white font-medium' : 'text-[var(--text-secondary)]'}`}>{label}</span>
      {value && <span className="text-[var(--text-tertiary)]">{value}</span>}
      {large && (
        <span className={`ml-auto ${isHealthy ? 'text-green-400' : 'text-red-400'}`}>
          {isHealthy ? 'Healthy' : 'Unhealthy'}
        </span>
      )}
    </div>
  );
}

/**
 * Simple Line Chart Component (SVG-based)
 */
function SimpleLineChart({ data, dataKey, color = '#3b82f6' }) {
  if (!data || data.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-[var(--text-tertiary)]">
        No data available
      </div>
    );
  }

  const values = data.map(d => d[dataKey] || 0);
  const maxValue = Math.max(...values, 1);
  const minValue = Math.min(...values);
  const range = maxValue - minValue || 1;
  
  const width = 100;
  const height = 100;
  const padding = 5;
  
  // Generate path points
  const points = data.map((d, i) => {
    const x = padding + (i / (data.length - 1)) * (width - 2 * padding);
    const y = height - padding - ((d[dataKey] - minValue) / range) * (height - 2 * padding);
    return `${x},${y}`;
  }).join(' ');

  // Generate area path
  const areaPoints = [
    `${padding},${height - padding}`,
    ...data.map((d, i) => {
      const x = padding + (i / (data.length - 1)) * (width - 2 * padding);
      const y = height - padding - ((d[dataKey] - minValue) / range) * (height - 2 * padding);
      return `${x},${y}`;
    }),
    `${width - padding},${height - padding}`
  ].join(' ');

  return (
    <div className="relative h-full">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full h-full"
        preserveAspectRatio="none"
      >
        {/* Grid lines */}
        {[0, 25, 50, 75, 100].map((y) => (
          <line
            key={y}
            x1={padding}
            y1={(y / 100) * (height - 2 * padding) + padding}
            x2={width - padding}
            y2={(y / 100) * (height - 2 * padding) + padding}
            stroke="rgba(255,255,255,0.1)"
            strokeWidth="0.3"
          />
        ))}
        
        {/* Area fill */}
        <polygon
          points={areaPoints}
          fill={`url(#gradient-${dataKey})`}
          opacity="0.3"
        />
        
        {/* Line */}
        <polyline
          points={points}
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        
        {/* Data points */}
        {data.map((d, i) => {
          const x = padding + (i / (data.length - 1)) * (width - 2 * padding);
          const y = height - padding - ((d[dataKey] - minValue) / range) * (height - 2 * padding);
          return (
            <circle
              key={i}
              cx={x}
              cy={y}
              r="1.5"
              fill={color}
            />
          );
        })}
        
        {/* Gradient definition */}
        <defs>
          <linearGradient id={`gradient-${dataKey}`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={color} stopOpacity="0.5" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>
      
      {/* Y-axis labels */}
      <div className="absolute left-0 top-0 bottom-0 flex flex-col justify-between text-[var(--text-tertiary)] text-xs py-1">
        <span>{maxValue}</span>
        <span>{Math.round((maxValue + minValue) / 2)}</span>
        <span>{minValue}</span>
      </div>
      
      {/* X-axis labels (show first and last) */}
      <div className="absolute bottom-0 left-8 right-0 flex justify-between text-[var(--text-tertiary)] text-xs">
        <span>{data[0]?.date?.slice(5) || ''}</span>
        <span>{data[data.length - 1]?.date?.slice(5) || ''}</span>
      </div>
    </div>
  );
}
